/**
 * 任务队列2.0 类型定义
 * 
 * @description 定义任务队列系统的核心类型
 * @author Frontend Architect
 * @version 2.0.0
 */

/** 生图进度状态码 */
export enum GenerationStatus {
  /** 错误或取消 */
  ERROR_OR_CANCELLED = -1,
  /** 排队中 */
  QUEUING = 1,
  /** 生成中 */
  GENERATING = 2,
  /** 已完成 */
  COMPLETED = 3
}

/** 用户类型枚举 */
export enum UserType {
  /** 免费用户 */
  FREE = 'free',
  /** 标准版会员 */
  VIP = 'vip',
  /** 专业版会员 */
  SVIP = 'svip'
}

/** 队列阶段类型 */
export enum QueueStage {
  /** 启动中 */
  INITIALIZING = 'initializing',
  /** 排队中 */
  QUEUING = 'queuing',
  /** 模型加载中 */
  MODEL_LOADING = 'model_loading',
  /** 生图中 */
  GENERATING = 'generating',
  /** 已完成 */
  COMPLETED = 'completed',
  /** 错误 */
  ERROR = 'error'
}

/** 队列任务接口 */
export interface QueueTask {
  /** 客户端ID */
  clientId: string;
  /** 模块类型 */
  module: number;
  /** 当前阶段 */
  stage: QueueStage;
  /** 生图状态 */
  status: GenerationStatus;
  /** 用户类型 */
  userType: UserType;
  /** 排队数量 */
  queueNum: number;
  /** 总排队数量 */
  queueTotalNum: number;
  /** 进度百分比 */
  progress: number;
  /** 状态消息 */
  message: string;
  /** 预计等待时间(秒) */
  estimatedWaitTime: number;
  /** 总等待时间(秒) */
  totalWaitTime: number;
  /** 任务创建时间 */
  createdAt: number;
  /** 阶段开始时间 */
  stageStartTime: number;
  /** 是否使用新队列 */
  useNewQueue: boolean;
}

/** 阶段配置接口 */
export interface StageConfig {
  /** 阶段名称 */
  name: string;
  /** 最大等待时间(秒) */
  maxWaitTime: number;
  /** 是否计入总等待时间 */
  countToTotalTime: boolean;
  /** 阶段特定配置 */
  config?: Record<string, any>;
}

/** 用户类型策略配置 */
export interface UserTypeStrategy {
  /** 用户类型 */
  userType: UserType;
  /** 总最大等待时间(秒) */
  maxTotalWaitTime: number;
  /** 各阶段配置 */
  stages: Record<QueueStage, StageConfig>;
}

/** 排队减少配置 */
export interface QueueReductionConfig {
  /** 减少间隔(毫秒) */
  interval: number;
  /** 减少百分比范围 */
  reductionPercentageRange: [number, number];
  /** 最小减少数量 */
  minReduction: number;
}

/** 埋点事件数据 */
export interface TrackingEventData {
  /** 事件类型 */
  eventType: 'queue_enter' | 'stage_change' | 'queue_reduction' | 'generation_complete' | 'user_exit';
  /** 客户端ID */
  clientId: string;
  /** 用户类型 */
  userType: UserType;
  /** 模块类型 */
  module: number;
  /** 时间戳 */
  timestamp: number;
  /** 事件特定数据 */
  data: Record<string, any>;
}

/** 队列状态更新回调类型 */
export interface QueueCallbacks {
  /** 状态更新回调 */
  onUpdate?: (task: QueueTask) => void;
  /** 阶段变更回调 */
  onStageChange?: (task: QueueTask, previousStage: QueueStage) => void;
  /** 进度更新回调 */
  onProgressUpdate?: (task: QueueTask, progress: number) => void;
  /** 完成回调 */
  onComplete?: (task: QueueTask) => void;
  /** 错误回调 */
  onError?: (task: QueueTask, error: Error) => void;
  /** 埋点回调 */
  onTrack?: (eventData: TrackingEventData) => void;
}

/** 队列管理器配置 */
export interface QueueManagerConfig {
  /** 用户类型策略映射 */
  userStrategies: Record<UserType, UserTypeStrategy>;
  /** 排队减少配置 */
  queueReduction: QueueReductionConfig;
  /** 埋点配置 */
  tracking: {
    enabled: boolean;
    endpoint?: string;
  };
  /** 调试模式 */
  debug: boolean;
}

/** 队列任务创建参数 */
export interface CreateQueueTaskParams {
  /** 客户端ID */
  clientId: string;
  /** 模块类型 */
  module: number;
  /** 用户类型 */
  userType: UserType;
  /** 是否使用新队列 */
  useNewQueue: boolean;
  /** 回调函数 */
  callbacks?: QueueCallbacks;
  /** 尺寸比例(用于万能渲染区分极速/高清) */
  aspectRatios?: string;
} 